import { CalendarAltIcon, EditAltIcon } from 'cokitchen-icons';
import { CareerPostingType } from 'models/career';
import moment from 'moment';
import React, { useState } from 'react';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';

import { Link, useNavigate } from 'react-router-dom';
import DeleteModal from 'components/base/delete-modal';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { ROUTE_KEYS } from 'utils/constants';
import { ENUM_JOB_STATUS } from 'utils/enum';

const CareerCard = ({
  careerPosting,
  refetchAllPosting
}: {
  careerPosting: CareerPostingType;
  refetchAllPosting: () => void;
}) => {
  const [showArchivePostModal, setShowArchivePostModal] = useState(false);
  const [postingUpdateLoading, setPostingUpdateLoading] = useState(false);
  const [publishRole, setPublishRole] = useState(false);
  const navigate = useNavigate();
  const handleUpdatePosting = async () => {
    setPostingUpdateLoading(true);

    try {
      await apiService.put(`careers/jobs/${careerPosting.id}`, {
        data: { status: publishRole ? ENUM_JOB_STATUS.open : ENUM_JOB_STATUS.archived }
      });
      refetchAllPosting();
      toast(
        <ToastMessage
          type='success'
          message={`Job ${
            publishRole ? ENUM_JOB_STATUS.open : ENUM_JOB_STATUS.archived
          } successfully`}
        />
      );
    } catch {
      toast(
        <ToastMessage
          type='error'
          message={`There was a problem ${publishRole ? 'publishing' : 'archiving'} this position `}
        />
      );
    } finally {
      setPostingUpdateLoading(false);
    }
  };
  const requiredFields = [
    careerPosting.role,
    careerPosting.department,
    careerPosting.work_mode,
    careerPosting.experience_level,
    careerPosting.locations,
    careerPosting.description,
    careerPosting.responsibilities,
    careerPosting.qualifications,
    careerPosting.application_deadline,
    careerPosting.no_of_openings
  ];
  const isDisabled = requiredFields.some((value) => {
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'number') return value <= 0;
    return !value;
  });

  return (
    <div className='relative border-gray-1/80 rounded-md border p-6 group'>
      <div className='absolute top-4 right-4 flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity'>
        <button
          onClick={() =>
            navigate(`/career/${careerPosting.id}/${ROUTE_KEYS.CAREER_DETAILS_PAGE}?edit=true`)
          }
          aria-label='Edit career posting'
          className='border border-gray-1/70 rounded-full p-2 bg-white shadow-md'>
          <EditAltIcon className='w-3 h-3 opacity-80' />
        </button>
        {careerPosting.status !== ENUM_JOB_STATUS.archived && (
          <button
            onClick={() => setShowArchivePostModal(true)}
            aria-label='Archive career posting'
            className='border border-gray-1/70 rounded-full p-2 bg-white shadow-md'>
            <ArchiveIcon className='w-3 h-3 opacity-80' />
          </button>
        )}
      </div>

      <div>
        <h3 className='text-lg font-medium'>{careerPosting.role}</h3>
        <p className='text-gray-5 text-sm'>{careerPosting.department}</p>
      </div>

      <hr className='h-1 w-full border-gray-1/70 my-3 mt-4' />

      <p className='text-gray-5 text-xs flex items-center gap-2'>
        <CalendarAltIcon />
        Added {moment(new Date(careerPosting.created_at)).format('ddd MMM DD, yyyy')}
      </p>

      <div className='mt-10 grid grid-cols-3 gap-2.5'>
        <Link
          className={`flex-1 block text-center border border-gray-1 rounded p-2 text-gray-6/70 text-sm font-medium ${
            careerPosting.status !== ENUM_JOB_STATUS.open ? 'col-span-2' : 'col-span-3'
          }`}
          to={`/career/${careerPosting.id}/${ROUTE_KEYS.CAREER_DETAILS_PAGE}`}>
          View details
        </Link>
        {careerPosting.status !== ENUM_JOB_STATUS.open && (
          <button
            disabled={isDisabled}
            onClick={() => setPublishRole(true)}
            className='flex-1 block text-center border bg-red-5 rounded p-2 text-white text-xs font-semibold active:scale-90 transition-all hover:opacity-90 disabled:bg-gray-4 disabled:cursor-not-allowed disabled:hover:opacity-100'>
            Publish
          </button>
        )}
      </div>

      <DeleteModal
        onClose={() => setShowArchivePostModal(false)}
        show={showArchivePostModal}
        description={`Are you sure you want to archive ${careerPosting.role}?`}
        handleDelete={handleUpdatePosting}
        loading={postingUpdateLoading}
        title={`Archive  ${careerPosting.role}?`}
        type='archive'
      />

      <DeleteModal
        onClose={() => setPublishRole(false)}
        show={publishRole}
        description={`Are you sure you want to publish ${careerPosting.role}?`}
        handleDelete={handleUpdatePosting}
        loading={postingUpdateLoading}
        title={`Publish  ${careerPosting.role}?`}
        type='publish'
        emoji='🥳'
      />
    </div>
  );
};

export default CareerCard;
